import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import { NEWSLETTER } from "@/constants/testIds";
import { API } from "@/lib/config";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    setMessage("");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus("success");
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setMessage(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again.",
      );
    }
  };

  return (
    <section
      id="newsletter"
      data-testid={NEWSLETTER.root}
      className="relative text-[#e8ecef] py-32 md:py-48"
      style={{
        backgroundImage:
          "linear-gradient(to bottom, #14304a 0%, #0a0a0a 25%, #0a0a0a 75%, #0f1216 100%)",
      }}
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <Reveal>
          <p className="text-[11px] tracking-[0.38em] uppercase text-[#8a9498] mb-10">
            (07) — Stay Connected
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl mx-auto">
            Letters from<br />
            <span className="font-light text-[#8a9498]">
              a slower life.
            </span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-[#8a9498] text-base md:text-lg max-w-xl mx-auto leading-relaxed font-light">
            Exclusive drops, journal entries, playlists, wellness notes, and
            travel inspiration — delivered rarely, and only when it matters.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <form
            data-testid={NEWSLETTER.form}
            onSubmit={onSubmit}
            className="mt-14 md:mt-16 mx-auto max-w-xl"
          >
            <div className="flex items-center border-b border-[#8a9498]/40 focus-within:border-[#e8ecef] transition-colors duration-500">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid={NEWSLETTER.input}
                className="flex-1 bg-transparent border-0 outline-none py-4 text-[#e8ecef] placeholder:text-[#8a9498]/50 text-base md:text-lg font-light"
                aria-label="Email address"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid={NEWSLETTER.submit}
                className="group inline-flex items-center gap-2 py-4 pl-4 text-[11px] tracking-[0.3em] uppercase text-[#e8ecef]/90 hover:text-[#e8ecef] transition-colors duration-500 disabled:opacity-50"
              >
                {status === "success" ? (
                  <>
                    Subscribed <Check size={14} />
                  </>
                ) : (
                  <>
                    {status === "loading" ? "Sending" : "Subscribe"}
                    <ArrowRight
                      size={14}
                      className="transition-transform duration-500 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>

            {status === "success" && (
              <p
                data-testid={NEWSLETTER.success}
                className="mt-6 text-sm text-[#8a9498] font-light"
              >
                You&rsquo;re in. Welcome to VISION.
              </p>
            )}
            {status === "error" && (
              <p
                data-testid={NEWSLETTER.error}
                className="mt-6 text-sm text-red-300/90 font-light"
              >
                {message || "Something went wrong. Please try again."}
              </p>
            )}
            <p className="mt-5 text-[10px] tracking-[0.28em] uppercase text-[#8a9498]/60">
              No noise. Unsubscribe any time.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
